import React, { useState, useEffect } from 'react';
import { getUsers, updateUser, resetUserPassword, deleteUser } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Modal from './Modal';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  PencilIcon,
  TrashIcon,
  KeyIcon,
  UserGroupIcon,
  CheckIcon,
  XMarkIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

const AccessManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    role: 'accountant',
    is_active: true,
    vendors_view: true,
    vendors_edit: false,
    invoices_view: true,
    invoices_edit: false,
    invoices_approve: false,
    invoices_delete: false,
    projects_view: false,
    projects_edit: false,
    companies_view: false,
    companies_edit: false,
    employees_view: false,
    employees_edit: false,
    users_manage: false,
    customers_view: false,
    customers_edit: false,
    ar_invoices_view: false,
    ar_invoices_edit: false,
    channels_view: false,
    channels_edit: false,
  });

  const [newPassword, setNewPassword] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});

  // Permission group definitions
  const permissionGroups = [
    {
      id: 'vendors',
      label: 'Vendors',
      permissions: [
        { key: 'vendors_view', label: 'View Vendors' },
        { key: 'vendors_edit', label: 'Edit Vendors' },
      ]
    },
    {
      id: 'invoices',
      label: 'AP Invoices',
      permissions: [
        { key: 'invoices_view', label: 'View AP Invoices' },
        { key: 'invoices_edit', label: 'Edit AP Invoices' },
        { key: 'invoices_approve', label: 'Approve AP Invoices' },
        { key: 'invoices_delete', label: 'Delete AP Invoices' },
      ]
    },
    {
      id: 'customers',
      label: 'Customers',
      permissions: [
        { key: 'customers_view', label: 'View Customer Management' },
        { key: 'customers_edit', label: 'Edit Customer Management' },
        { key: 'ar_invoices_view', label: 'View AR Invoices' },
        { key: 'ar_invoices_edit', label: 'Edit AR Invoices' },
        { key: 'channels_view', label: 'View Channels & Reports' },
        { key: 'channels_edit', label: 'Edit Channels & Reports' },
      ]
    },
    {
      id: 'projects',
      label: 'Projects',
      permissions: [
        { key: 'projects_view', label: 'View Projects' },
        { key: 'projects_edit', label: 'Edit Projects' },
      ]
    },
    {
      id: 'companies',
      label: 'Companies',
      permissions: [
        { key: 'companies_view', label: 'View Companies' },
        { key: 'companies_edit', label: 'Edit Companies' },
      ]
    },
    {
      id: 'employees',
      label: 'Employees',
      permissions: [
        { key: 'employees_view', label: 'View Employees' },
        { key: 'employees_edit', label: 'Edit Employees' },
      ]
    },
    {
      id: 'administration',
      label: 'Administration',
      permissions: [
        { key: 'users_manage', label: 'Manage Users' },
      ]
    },
  ];

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const isGroupAllSelected = (group) => {
    return group.permissions.every(p => formData[p.key]);
  };

  const isGroupPartiallySelected = (group) => {
    const selected = group.permissions.filter(p => formData[p.key]).length;
    return selected > 0 && selected < group.permissions.length;
  };

  const toggleGroupAll = (group) => {
    const allSelected = isGroupAllSelected(group);
    const newFormData = { ...formData };
    group.permissions.forEach(p => {
      newFormData[p.key] = !allSelected;
    });
    setFormData(newFormData);
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await getUsers();
      setUsers(response.data);
    } catch (error) {
      toast.error('Error loading users');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        password: '',
        full_name: user.full_name,
        role: user.role,
        is_active: Boolean(user.is_active),
        vendors_view: Boolean(user.vendors_view),
        vendors_edit: Boolean(user.vendors_edit),
        invoices_view: Boolean(user.invoices_view),
        invoices_edit: Boolean(user.invoices_edit),
        invoices_approve: Boolean(user.invoices_approve),
        invoices_delete: Boolean(user.invoices_delete),
        projects_view: Boolean(user.projects_view),
        projects_edit: Boolean(user.projects_edit),
        companies_view: Boolean(user.companies_view),
        companies_edit: Boolean(user.companies_edit),
        employees_view: Boolean(user.employees_view),
        employees_edit: Boolean(user.employees_edit),
        users_manage: Boolean(user.users_manage),
        customers_view: Boolean(user.customers_view),
        customers_edit: Boolean(user.customers_edit),
        ar_invoices_view: Boolean(user.ar_invoices_view),
        ar_invoices_edit: Boolean(user.ar_invoices_edit),
        channels_view: Boolean(user.channels_view),
        channels_edit: Boolean(user.channels_edit),
      });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingUser(null);
  };

  const handleRoleChange = (role) => {
    let newPermissions = { ...formData, role };

    // Set default permissions based on role
    if (role === 'admin') {
      newPermissions = {
        ...newPermissions,
        vendors_view: true,
        vendors_edit: true,
        invoices_view: true,
        invoices_edit: true,
        invoices_approve: true,
        invoices_delete: true,
        projects_view: true,
        projects_edit: true,
        companies_view: true,
        companies_edit: true,
        employees_view: true,
        employees_edit: true,
        users_manage: true,
        customers_view: true,
        customers_edit: true,
        ar_invoices_view: true,
        ar_invoices_edit: true,
        channels_view: true,
        channels_edit: true,
      };
    } else if (role === 'hr') {
      newPermissions = {
        ...newPermissions,
        vendors_view: true,
        vendors_edit: false,
        invoices_view: false,
        invoices_edit: false,
        invoices_approve: false,
        invoices_delete: false,
        projects_view: false,
        projects_edit: false,
        companies_view: false,
        companies_edit: false,
        employees_view: true,
        employees_edit: true,
        users_manage: false,
        customers_view: false,
        customers_edit: false,
        ar_invoices_view: false,
        ar_invoices_edit: false,
        channels_view: false,
        channels_edit: false,
      };
    } else if (role === 'manager') {
      newPermissions = {
        ...newPermissions,
        vendors_view: true,
        vendors_edit: true,
        invoices_view: true,
        invoices_edit: true,
        invoices_approve: true,
        invoices_delete: true,
        projects_view: true,
        projects_edit: true,
        companies_view: true,
        companies_edit: false,
        employees_view: true,
        employees_edit: true,
        users_manage: false,
        customers_view: true,
        customers_edit: true,
        ar_invoices_view: true,
        ar_invoices_edit: true,
        channels_view: true,
        channels_edit: true,
      };
    } else {
      newPermissions = {
        ...newPermissions,
        vendors_view: true,
        vendors_edit: true,
        invoices_view: true,
        invoices_edit: true,
        invoices_approve: false,
        invoices_delete: false,
        projects_view: false,
        projects_edit: false,
        companies_view: true,
        companies_edit: false,
        employees_view: false,
        employees_edit: false,
        users_manage: false,
        customers_view: false,
        customers_edit: false,
        ar_invoices_view: false,
        ar_invoices_edit: false,
        channels_view: false,
        channels_edit: false,
      };
    }

    setFormData(newPermissions);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSaving(true);
    try {
      // Only update role and permissions, not user details
      const updateData = {
        role: formData.role,
        vendors_view: formData.vendors_view,
        vendors_edit: formData.vendors_edit,
        invoices_view: formData.invoices_view,
        invoices_edit: formData.invoices_edit,
        invoices_approve: formData.invoices_approve,
        invoices_delete: formData.invoices_delete,
        projects_view: formData.projects_view,
        projects_edit: formData.projects_edit,
        companies_view: formData.companies_view || false,
        companies_edit: formData.companies_edit || false,
        employees_view: formData.employees_view || false,
        employees_edit: formData.employees_edit || false,
        users_manage: formData.users_manage,
        customers_view: formData.customers_view || false,
        customers_edit: formData.customers_edit || false,
        ar_invoices_view: formData.ar_invoices_view || false,
        ar_invoices_edit: formData.ar_invoices_edit || false,
        channels_view: formData.channels_view || false,
        channels_edit: formData.channels_edit || false,
      };

      await updateUser(editingUser.id, updateData);
      toast.success('Permissions updated successfully');
      closeModal();
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error updating permissions');
    } finally {
      setSaving(false);
    }
  };

  const openPasswordModal = (userId) => {
    setSelectedUserId(userId);
    setNewPassword('');
    setPasswordModalOpen(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSaving(true);
    try {
      await resetUserPassword(selectedUserId, newPassword);
      toast.success('Password reset successfully');
      setPasswordModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error resetting password');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (user.id === currentUser.id) {
      toast.error('You cannot delete your own account');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${user.full_name}"?`)) {
      return;
    }

    try {
      await deleteUser(user.id);
      toast.success('User deleted successfully');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error deleting user');
    }
  };

  const PermissionBadge = ({ allowed }) => (
    <span style={{
      color: allowed ? 'var(--color-accent-success)' : 'var(--color-text-tertiary)',
      display: 'inline-flex',
      alignItems: 'center'
    }}>
      {allowed ? <CheckIcon style={{ width: 16, height: 16 }} /> : <XMarkIcon style={{ width: 16, height: 16 }} />}
    </span>
  );

  if (loading) {
    return <div className="loading-overlay"><div className="spinner" /><span>Loading users...</span></div>;
  }

  return (
    <div>
      <div style={{ marginBottom: 'var(--space-lg)' }}>
        <h2 style={{ margin: 0, fontSize: '1.25rem' }}>User Access Management</h2>
        <p style={{ margin: '0.5rem 0 0 0', color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
          Manage user roles and permissions. Users are created automatically when adding employees.
        </p>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th style={{ textAlign: 'center' }}>Vendors</th>
                <th style={{ textAlign: 'center' }}>Invoices</th>
                <th style={{ textAlign: 'center' }}>Approve</th>
                <th style={{ textAlign: 'center' }}>Projects</th>
                <th style={{ textAlign: 'center' }}>Users</th>
                <th>Created</th>
                <th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div>
                      <strong>{user.full_name}</strong>
                      <div style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
                        {user.username} • {user.email}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${
                      user.role === 'admin' ? 'badge-sent' :
                      user.role === 'manager' ? 'badge-approved' : 'badge-draft'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${user.is_active ? 'badge-approved' : 'badge-rework'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <PermissionBadge allowed={user.vendors_view || user.vendors_edit} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <PermissionBadge allowed={user.invoices_view || user.invoices_edit} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <PermissionBadge allowed={user.invoices_approve} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <PermissionBadge allowed={user.projects_view || user.projects_edit} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <PermissionBadge allowed={user.users_manage} />
                  </td>
                  <td>{format(new Date(user.created_at), 'MMM d, yyyy')}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-ghost btn-icon btn-sm" onClick={() => openModal(user)} title="Edit">
                        <PencilIcon style={{ width: 16, height: 16 }} />
                      </button>
                      <button className="btn btn-ghost btn-icon btn-sm" onClick={() => openPasswordModal(user.id)} title="Reset Password">
                        <KeyIcon style={{ width: 16, height: 16 }} />
                      </button>
                      {user.id !== currentUser.id && (
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          onClick={() => handleDelete(user)}
                          title="Delete"
                          style={{ color: 'var(--color-accent-danger)' }}
                        >
                          <TrashIcon style={{ width: 16, height: 16 }} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {users.length === 0 && (
          <div className="empty-state">
            <UserGroupIcon className="empty-state-icon" />
            <p className="empty-state-title">No users found</p>
          </div>
        )}
      </div>

      {/* User Form Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title="Edit User Permissions"
        size="lg"
        footer={
          <>
            <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : 'Update Permissions'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          {/* User Details - Read Only */}
          <div style={{ marginBottom: 'var(--space-lg)', padding: 'var(--space-md)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-md)' }}>
            <h4 style={{ marginBottom: 'var(--space-md)', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>User Details</h4>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Username</label>
                <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-primary)', borderRadius: 'var(--radius-sm)', color: 'var(--color-text-secondary)' }}>
                  {formData.username}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-primary)', borderRadius: 'var(--radius-sm)', color: 'var(--color-text-secondary)' }}>
                  {formData.full_name}
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Email</label>
                <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-primary)', borderRadius: 'var(--radius-sm)', color: 'var(--color-text-secondary)' }}>
                  {formData.email}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <div style={{ padding: 'var(--space-sm)' }}>
                  <span className={`badge ${formData.is_active ? 'badge-approved' : 'badge-rework'}`}>
                    {formData.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span style={{ marginLeft: 'var(--space-sm)', fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
                    (Managed in Employees)
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Role Selection */}
          <div className="form-group" style={{ marginBottom: 'var(--space-lg)' }}>
            <label className="form-label">Role</label>
            <select
              className="form-select"
              value={formData.role}
              onChange={(e) => handleRoleChange(e.target.value)}
            >
              <option value="accountant">Accountant</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
              <option value="hr">HR</option>
            </select>
          </div>

          <div style={{ marginTop: 'var(--space-lg)', padding: 'var(--space-md)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-md)' }}>
            <h4 style={{ marginBottom: 'var(--space-md)', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>Permissions</h4>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {permissionGroups.map((group) => {
                const isExpanded = expandedGroups[group.id];
                const allSelected = isGroupAllSelected(group);
                const partiallySelected = isGroupPartiallySelected(group);
                const selectedCount = group.permissions.filter(p => formData[p.key]).length;

                return (
                  <div key={group.id} style={{ border: '1px solid var(--color-border-primary)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                    {/* Group Header Row */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: 'var(--space-sm) var(--space-md)',
                        background: isExpanded ? 'var(--color-bg-secondary)' : 'var(--color-bg-primary)',
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                      onClick={() => toggleGroup(group.id)}
                    >
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon btn-sm"
                        style={{ marginRight: 'var(--space-sm)', padding: '2px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleGroup(group.id);
                        }}
                      >
                        {isExpanded ? (
                          <ChevronDownIcon style={{ width: 16, height: 16 }} />
                        ) : (
                          <ChevronRightIcon style={{ width: 16, height: 16 }} />
                        )}
                      </button>

                      <span style={{ flex: 1, fontWeight: 500, fontSize: '0.9rem' }}>
                        {group.label}
                      </span>

                      <span style={{
                        fontSize: '0.8rem',
                        color: selectedCount > 0 ? 'var(--color-accent-success)' : 'var(--color-text-tertiary)',
                        marginRight: 'var(--space-md)'
                      }}>
                        {selectedCount}/{group.permissions.length}
                      </span>

                      <label
                        className="form-checkbox"
                        style={{ margin: 0 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = partiallySelected;
                          }}
                          onChange={() => toggleGroupAll(group)}
                        />
                        <span style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>Select All</span>
                      </label>
                    </div>

                    {/* Expanded Permissions */}
                    {isExpanded && (
                      <div style={{
                        padding: 'var(--space-sm) var(--space-md)',
                        paddingLeft: 'calc(var(--space-md) + 32px)',
                        background: 'var(--color-bg-primary)',
                        borderTop: '1px solid var(--color-border-primary)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--space-xs)',
                      }}>
                        {group.permissions.map((permission) => (
                          <label key={permission.key} className="form-checkbox" style={{ margin: 0 }}>
                            <input
                              type="checkbox"
                              checked={formData[permission.key]}
                              onChange={(e) => setFormData({ ...formData, [permission.key]: e.target.checked })}
                            />
                            <span>{permission.label}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </form>
      </Modal>

      {/* Password Reset Modal */}
      <Modal
        isOpen={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        title="Reset Password"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setPasswordModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleResetPassword} disabled={saving}>
              {saving ? 'Resetting...' : 'Reset Password'}
            </button>
          </>
        }
      >
        <div className="form-group mb-0">
          <label className="form-label">New Password</label>
          <input
            type="password"
            className="form-input"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password (min 6 characters)"
          />
        </div>
      </Modal>
    </div>
  );
};

export default AccessManagement;
